/* eslint-disable prettier/prettier */
import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Chip from '@material-ui/core/Chip';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { BASE_S3 } from 'config/consts';

const VendasOnlineParceiroPreview = ({ formValues, activeSection }) => {
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const imagePath = formValues?.image 
    ? (formValues.image.startsWith('http') ? formValues.image : `${BASE_S3}/${formValues.image}`)
    : null;

  // Função para obter imagem do produto
  const getProductImage = (product) => {
    if (!product?.image) return null;
    return product.image.startsWith('http') ? product.image : `${BASE_S3}/${product.image}`;
  };

  // Obter métodos de pagamento
  const paymentMethods = formValues?.payment_methods || [];

  const renderActivityPreview = () => (
    <Box style={{ padding: 16 }}>
      {/* Imagem Principal */}
      <Box
        style={{
          width: '100%',
          paddingTop: '56.25%',
          position: 'relative',
          backgroundColor: '#f5f5f5',
          borderRadius: 8,
          overflow: 'hidden',
          marginBottom: 16,
        }}
      >
        {imagePath ? (
          <img
            src={imagePath}
            alt="Preview"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <Box
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              color: '#999',
            }}
          >
            <Typography variant="body2">Clique para subir a imagem para seu post</Typography>
            <Typography variant="caption">Medidas: 960px por 540px</Typography>
            <Typography variant="caption" display="block">PNG, JPG ou GIF</Typography>
          </Box>
        )}
      </Box>

      {/* Título */}
      <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: 8 }}>
        {formValues?.body_name || 'Nome do serviço e algum diferencial'}
      </Typography>

      {/* Descrição - Sempre visível */}
      <Box 
        style={{ 
          marginBottom: 16,
          padding: 12,
          backgroundColor: '#fafafa',
          borderRadius: 4,
          minHeight: 60,
        }}
      >
        {formValues?.body_description ? (
          <div 
            dangerouslySetInnerHTML={{ 
              __html: formValues.body_description 
            }}
            style={{ fontSize: 14, lineHeight: 1.5 }}
          />
        ) : (
          <Typography variant="body2" color="textSecondary">
            Informações iniciais sobre o serviço
          </Typography>
        )}
      </Box>

      {/* Produtos */}
      {formValues?.products?.length > 0 && (
        <Box style={{ marginBottom: 16 }}>
          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Produtos:</Typography>
          <Grid container spacing={2}>
            {formValues.products.map((product, index) => {
              const productImage = getProductImage(product);
              return (
                <Grid item xs={12} key={index}>
                  <Paper style={{ overflow: 'hidden' }}>
                    {/* Imagem do Produto - Maior */}
                    {productImage && (
                      <Box
                        style={{
                          width: '100%',
                          height: 150,
                          backgroundImage: `url(${productImage})`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                        }}
                      />
                    )}
                    <Box style={{ padding: 12 }}>
                      <Typography variant="body1" style={{ fontWeight: 'bold' }}>
                        {product.name || `Produto ${index + 1}`}
                      </Typography>
                      {product.description && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {product.description}
                        </Typography>
                      )}
                      <Typography variant="h6" color="primary" style={{ marginTop: 8 }}>
                        R$ {(parseFloat(product.price) || 0).toFixed(2).replace('.', ',')}
                      </Typography>
                      {product.stock && (
                        <Typography variant="caption" display="block">
                          Estoque: {product.stock} unidades
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Informações de pagamento */}
      <Box style={{ marginTop: 16, padding: 16, backgroundColor: '#f9f9f9', borderRadius: 4 }}>
        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>Formas de Pagamento:</Typography>
        <Box style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {paymentMethods.includes('pix') && <Chip label="PIX" size="small" color="primary" />}
          {paymentMethods.includes('credit_card') && <Chip label="Cartão de Crédito" size="small" color="primary" />}
          {paymentMethods.includes('boleto') && <Chip label="Boleto" size="small" color="primary" />}
          {paymentMethods.length === 0 && (
            <Typography variant="caption" color="textSecondary">
              Nenhum método selecionado
            </Typography>
          )}
        </Box>
        {formValues?.allow_installments && (
          <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
            Parcelamento em até {formValues.installments_count || 12}x
          </Typography>
        )}
        {formValues?.recurrence_type && formValues.recurrence_type !== 'single' && (
          <Typography variant="caption" display="block" style={{ marginTop: 8 }}>
            Recorrência: {formValues.recurrence_type}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderCardPreview = () => (
    <Box style={{ padding: 16 }}>
      <Paper
        style={{
          width: '100%',
          maxWidth: 300,
          margin: '0 auto',
          borderRadius: 8,
          overflow: 'hidden',
        }}
      >
        <Box
          style={{
            width: '100%',
            paddingTop: '100%',
            position: 'relative',
            backgroundColor: '#f5f5f5',
          }}
        >
          {imagePath && (
            <img
              src={imagePath}
              alt="Card Preview"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )}
        </Box>
        <Box style={{ padding: 16 }}>
          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
            {formValues?.body_name || 'Nome da atividade'}
          </Typography>
          {formValues?.body_description ? (
            <div 
              dangerouslySetInnerHTML={{ __html: formValues.body_description }}
              style={{ fontSize: 12, color: '#666', marginTop: 4 }}
            />
          ) : (
            <Typography variant="caption" color="textSecondary">
              Resumo da atividade
            </Typography>
          )}
        </Box>
      </Paper>
    </Box>
  );

  return (
    <Box style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box style={{ padding: 16 }}>
        <Typography variant="h6">Preview</Typography>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="ATIVIDADE" />
          <Tab label="CARD" />
        </Tabs>
      </Box>

      <Box style={{ flex: 1, overflowY: 'auto' }}>
        {tabValue === 0 ? renderActivityPreview() : renderCardPreview()}
      </Box>
    </Box>
  );
};

VendasOnlineParceiroPreview.propTypes = {
  formValues: PropTypes.object,
  activeSection: PropTypes.number,
};

export default VendasOnlineParceiroPreview;
